/**
 * API Service Layer
 * 
 * Centralized HTTP client for communicating with the session backend.
 * In local mode: talks directly to localhost:8000.
 * In cloud mode: routes through the AWS backend proxy at /api/proxy/*.
 */

import { getAuthToken } from '../utils/auth';

const DEPLOYMENT_MODE = process.env.REACT_APP_DEPLOYMENT_MODE || 'local';
const CLOUD_API_URL = process.env.REACT_APP_CLOUD_API_URL || '';
const LOCAL_API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export function getApiBase() {
    if (DEPLOYMENT_MODE === 'cloud') {
        return `${CLOUD_API_URL}/api/proxy`;
    }
    return LOCAL_API_BASE;
}

function fetchWithTimeout(url, options = {}, timeoutMs = 30000) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(id));
}

/**
 * Generic fetch wrapper with error handling
 */
async function apiFetch(endpoint, options = {}) {
    const url = `${getApiBase()}${endpoint}`;
    const method = (options.method || 'GET').toUpperCase();

    const headers = { ...options.headers };
    if (['POST', 'PUT', 'PATCH'].includes(method)) {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    if (DEPLOYMENT_MODE === 'cloud') {
        const token = getAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }

    try {
        const response = await fetchWithTimeout(url, {
            ...options,
            headers,
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: response.statusText }));
            throw new Error(error.detail || `API Error: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`API Error [${endpoint}]:`, error);
        throw error;
    }
}

// ===== Project APIs =====

export const projectApi = {
    /**
     * List all projects
     */
    list: () => apiFetch('/api/projects'),

    /**
     * Get project details
     */
    get: (projectId) => apiFetch(`/api/projects/${projectId}`),

    /**
     * List project files
     */
    listFiles: (projectId, path = '', { showHidden = false } = {}) =>
        apiFetch(`/api/projects/${projectId}/files?path=${encodeURIComponent(path)}${showHidden ? '&show_hidden=true' : ''}`),

    /**
     * Read file content (text files only)
     */
    readFile: (projectId, path) =>
        apiFetch(`/api/projects/${projectId}/file?path=${encodeURIComponent(path)}`),

    /**
     * Get raw file URL (for images and binary files)
     */
    getRawFileUrl: (projectId, path) => {
        const base = `${getApiBase()}/api/projects/${projectId}/file/raw?path=${encodeURIComponent(path)}`;
        if (DEPLOYMENT_MODE === 'cloud') {
            const token = getAuthToken();
            if (token) return `${base}&token=${encodeURIComponent(token)}`;
        }
        return base;
    },

    /**
     * Upload a file attachment via HTTP (avoids WebSocket size limits).
     * Returns { path, name, type, isImage, size }.
     */
    uploadAttachment: async (projectId, file) => {
        const url = `${getApiBase()}/api/projects/${projectId}/attachments/upload`;
        const formData = new FormData();
        formData.append('file', file);

        const headers = {};
        if (DEPLOYMENT_MODE === 'cloud') {
            const token = getAuthToken();
            if (token) headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(url, { method: 'POST', headers, body: formData });
        const contentType = response.headers.get('content-type') || '';
        if (!response.ok) {
            const error = contentType.includes('application/json')
                ? await response.json().catch(() => ({ detail: response.statusText }))
                : { detail: `Upload failed: ${response.status} ${response.statusText}` };
            throw new Error(error.detail || `Upload failed: ${response.status}`);
        }
        if (!contentType.includes('application/json')) {
            throw new Error(
                `Upload endpoint returned unexpected content-type "${contentType}" — ` +
                `is the backend running? (URL: ${url})`
            );
        }
        return await response.json();
    },

    /**
     * Write file content
     */
    writeFile: (projectId, path, content) =>
        apiFetch(`/api/projects/${projectId}/file?path=${encodeURIComponent(path)}`, {
            method: 'PUT',
            body: JSON.stringify({ content }),
        }),

    /**
     * Delete file
     */
    deleteFile: (projectId, path) =>
        apiFetch(`/api/projects/${projectId}/file?path=${encodeURIComponent(path)}`, {
            method: 'DELETE',
        }),

    /**
     * Delete project
     */
    delete: (projectId) =>
        apiFetch(`/api/projects/${projectId}`, {
            method: 'DELETE',
        }),
    
    /**
     * Clone a project from AWS backend
     * @param {string} awsProjectId - UUID of project in AWS
     * @param {string} authToken - Optional JWT token
     */
    cloneFromAws: (awsProjectId, authToken = null) =>
        apiFetch('/api/projects/clone-from-aws', {
            method: 'POST',
            body: JSON.stringify({
                aws_project_id: awsProjectId,
                auth_token: authToken,
            }),
        }),
    
    /**
     * Get current git status for a project
     */
    getGitStatus: (projectId) =>
        apiFetch(`/api/projects/${projectId}/git/status`),
    
    /**
     * Commit all current changes locally
     * @param {string} projectId - Local project ID
     * @param {string} message - Commit message
     */
    commitChanges: (projectId, message = 'Agent changes') =>
        apiFetch(`/api/projects/${projectId}/git/commit?message=${encodeURIComponent(message)}`, {
            method: 'POST',
        }),
    
    /**
     * Push changes to AWS backend
     * @param {string} projectId - Local project ID
     * @param {string} message - Commit message
     * @param {string} authToken - Optional JWT token
     */
    pushToAws: (projectId, message = 'Agent changes', authToken = null) =>
        apiFetch(`/api/projects/${projectId}/git/push`, {
            method: 'POST',
            body: JSON.stringify({
                message,
                auth_token: authToken,
            }),
        }),
    
    /**
     * Revert project to a previous commit (undo operation)
     * @param {string} projectId - Local project ID
     * @param {string} commitSha - Commit SHA to revert to
     * @param {string} messageId - Optional AWS message ID
     * @param {string} authToken - Optional JWT token
     */
    revertToCommit: (projectId, commitSha, messageId = null, authToken = null) =>
        apiFetch(`/api/projects/${projectId}/git/revert`, {
            method: 'POST',
            body: JSON.stringify({
                commit_sha: commitSha,
                message_id: messageId,
                auth_token: authToken,
            }),
        }),
};

// ===== Vite APIs =====

export const viteApi = {
    /**
     * Check if npm is available on the system
     */
    checkNpm: () =>
        apiFetch('/api/vite/check-npm'),

    /**
     * Start Vite dev server
     */
    start: (projectId) =>
        apiFetch(`/api/vite/start?project_id=${projectId}`, {
            method: 'POST',
        }),

    /**
     * Stop Vite dev server for a specific project
     */
    stop: (projectId) =>
        apiFetch(`/api/vite/stop?project_id=${projectId}`, {
            method: 'POST',
        }),

    /**
     * Stop ALL running Vite dev servers
     */
    stopAll: () =>
        apiFetch('/api/vite/stop-all', {
            method: 'POST',
        }),

    /**
     * Get Vite status
     */
    status: (projectId) =>
        apiFetch(`/api/vite/status?project_id=${projectId}`),

    /**
     * Get Vite output logs
     */
    output: (projectId, lines = 50) =>
        apiFetch(`/api/vite/output?project_id=${projectId}&lines=${lines}`),
};

// ===== Agent APIs =====

export const agentApi = {
    /**
     * Execute agent (non-streaming)
     */
    execute: (projectId, task, agentConfig = null) =>
        apiFetch('/api/agent/execute', {
            method: 'POST',
            body: JSON.stringify({
                project_id: projectId,
                task,
                agent_config: agentConfig,
            }),
        }),

    /**
     * Get built-in tools
     */
    getBuiltinTools: () => apiFetch('/api/tools/builtin'),

    /**
     * Get available model providers
     */
    getProviders: () => apiFetch('/api/providers'),
};

// ===== Workflow APIs (existing) =====

export const workflowApi = {
    /**
     * Validate workflow definition
     */
    validate: (workflow) =>
        apiFetch('/api/workflow/validate', {
            method: 'POST',
            body: JSON.stringify(workflow),
        }),

    /**
     * Execute workflow (non-streaming)
     */
    execute: (workflow, task) =>
        apiFetch('/api/workflow/execute', {
            method: 'POST',
            body: JSON.stringify({ workflow, task, streaming: false }),
        }),

    /**
     * Check if a project has an active (running) workflow
     */
    checkActive: (projectId) =>
        apiFetch(`/api/workflow/active/${projectId}`),

    /**
     * Stop a running workflow for a project
     */
    stop: (projectId) =>
        apiFetch(`/api/workflow/stop/${projectId}`, { method: 'POST' }),
};

// ===== Message APIs (proxied through local backend) =====

export const messageApi = {
    /**
     * Get all messages for a project via the local backend gateway.
     * The local backend fetches from AWS (or its cache) and returns messages.
     * @param {string} projectId - Local project ID
     */
    getMessages: (projectId) => apiFetch(`/api/projects/${projectId}/messages`),
};

export default {
    project: projectApi,
    vite: viteApi,
    agent: agentApi,
    workflow: workflowApi,
    message: messageApi,
};
